// FILE: src/questionTypes/registry.js
//
// Centralized question-type registry (Case Study Question Framework, PR 1).
// Design: docs/case-study-framework-proposal.md (Rev 3), section 2.5.
//
// Each registered type implements a common contract:
//
//   {
//     type:            'multiple_choice',        // DB identifier (questions.type)
//     supported:       true,                      // has a learner renderer?
//     validate(q)      -> [errorStrings],         // authoring-time validation
//     normalizeAnswer(a) -> [trimmedStrings],     // canonical answer shape
//     score(q, a)      -> boolean,                // all-or-nothing (Phase 1)
//     AdminEditor / LearnerRenderer / ReviewRenderer, // wired in PR 2/3
//   }
//
// IMPORTANT — dual implementation: these normalize/score functions are the
// CANONICAL scoring spec. The SQL mirror lives in _case_score_answer
// (supabase/migrations/071_case_study_rpcs.sql) and both sides are verified
// against the shared fixtures in __fixtures__/scoringVectors.json so client
// preview (admin) and server grading cannot drift.
//
// Phase 1 scope guard: the registry is consumed only by the case-study
// runner, editor, and review surfaces. QuestionCard and the standard
// practice/exam flows are intentionally NOT rewritten onto it yet.

const _registry = new Map();

export function registerQuestionType(def) {
  if (!def || !def.type) {
    throw new Error('registerQuestionType: definition requires a `type`');
  }
  _registry.set(def.type, {
    supported: false,
    AdminEditor: null,
    LearnerRenderer: null,
    ReviewRenderer: null,
    validate: () => [],
    normalizeAnswer: (a) => normalizeAnswerArray(a),
    score: () => false,
    ...def,
  });
}

export function getQuestionType(type) {
  return _registry.get(type) || null;
}

export function isSupportedQuestionType(type) {
  const def = _registry.get(type);
  return !!(def && def.supported);
}

export function getRegisteredTypeIds() {
  return Array.from(_registry.keys());
}

/**
 * Attach renderer components to an already-registered type (PR 3). Keeping
 * component wiring separate from the logic definitions lets the logic module
 * stay import-light for services/scripts while UI code opts into renderers.
 */
export function attachRenderers(type, comps) {
  const def = _registry.get(type);
  if (!def) throw new Error(`attachRenderers: unknown type "${type}"`);
  Object.assign(def, comps);
}

/**
 * Shared canonical normalization: every Phase 1 answer is an array of
 * strings (choice ids, 'promptId:optionId' pairs, or ordered item ids).
 * Mirrors the btrim + array coercion in _case_score_answer.
 */
export function normalizeAnswerArray(answer) {
  if (!Array.isArray(answer)) return [];
  return answer.map((x) => String(x).trim()).filter((x) => x.length > 0);
}

/** Set equality on normalized arrays (order-insensitive). */
export function setsEqual(a, b) {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((x, i) => x === sb[i]);
}

/** Exact ordered equality on normalized arrays. */
export function arraysEqual(a, b) {
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

/**
 * Score any registered type against a raw answer. Returns false for
 * unregistered types (never throws in the scoring path).
 */
export function scoreAnswer(question, answer) {
  const def = _registry.get(question && question.type);
  if (!def) return false;
  return def.score(question, answer);
}
